import { createRoot } from 'react-dom/client';
import './index.css';
import { initSentry } from './lib/monitoring/sentry';

// Inicializa monitoreo (solo activo en producción/staging real)
initSentry();

// ===== Host-based bootstrap =====
// perfolab.dev / www.perfolab.dev  → Landing pública (bundle ligero, sin Supabase ni providers)
// cualquier otro host              → Aplicación operativa (App.tsx)
const host = window.location.hostname;
const pathname = window.location.pathname;
const LANDING_HOSTS = new Set(['perfolab.dev', 'www.perfolab.dev']);
const LANDING_PATHS = new Set(['/']);
const isLandingHost = LANDING_HOSTS.has(host) && LANDING_PATHS.has(pathname);

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('No se encontró el elemento root');
const root = createRoot(rootElement);

if (isLandingHost) {
  // Landing: NO registrar SW. Desregistrar cualquier SW previo de la PWA operativa
  // (caso usuario que instaló la app antes del split de dominios) y limpiar caches
  // para evitar servir la app vieja desde perfolab.dev.
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then(regs => {
      regs.forEach(r => r.unregister().catch(() => {}));
    }).catch(() => {});
  }
  if ('caches' in window) {
    caches.keys().then(keys => keys.forEach(k => caches.delete(k).catch(() => {}))).catch(() => {});
  }

  import('./landing/LandingApp').then(({ default: LandingApp }) => {
    root.render(<LandingApp />);
  });
} else {
  // Reintenta imports dinámicos (chunk viejo / dev-server reiniciado) antes de fallar.
  const retryImport = async <T,>(factory: () => Promise<T>): Promise<T> => {
    let lastErr: unknown;
    for (let i = 0; i < 3; i++) {
      try {
        return await factory();
      } catch (err) {
        lastErr = err;
        await new Promise((r) => setTimeout(r, 400 * (i + 1)));
      }
    }
    throw lastErr;
  };

  // Aplicación operativa — bootstrap original sin cambios funcionales
  Promise.all([
    retryImport(() => import('./App')),
    retryImport(() => import('@ionic/pwa-elements/loader')),
    retryImport(() => import('virtual:pwa-register')),
  ]).then(([{ default: App }, { defineCustomElements }, { registerSW }]) => {

    defineCustomElements(window);

    const isInIframe = (() => {
      try { return window.self !== window.top; } catch { return true; }
    })();
    const isPreviewHost =
      host.includes('id-preview--') ||
      host.includes('lovableproject.com') ||
      host === 'localhost' ||
      host === '127.0.0.1';

    if (isInIframe || isPreviewHost) {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations().then(regs => {
          regs.forEach(r => r.unregister().catch(() => {}));
        }).catch(() => {});
      }
      if ('caches' in window) {
        caches.keys().then(keys => keys.forEach(k => caches.delete(k).catch(() => {}))).catch(() => {});
      }
    } else {
      registerSW({
        immediate: true,
        onRegisteredSW(_swUrl, registration) {
          if (!registration) return;
          registration.update().catch(() => {});
          document.addEventListener('visibilitychange', () => {
            if (document.visibilityState === 'visible') {
              registration.update().catch(() => {});
            }
          });
          setInterval(() => registration.update().catch(() => {}), 10 * 60 * 1000);
        },
        onNeedRefresh() {
          try { localStorage.setItem('perfolab_update_pending', '1'); } catch {}
          console.log('[PWA] Nueva versión disponible — esperando confirmación del operador');
        },
        onRegisterError(error) {
          console.error('[PWA] SW registration error:', error);
        },
      });

      const checkVersionMismatch = async () => {
        try {
          const res = await fetch(`/version.json?ts=${Date.now()}`, { cache: 'no-store' });
          if (!res.ok) return;
          const remote = await res.json();
          const remoteId = remote?.buildDate || remote?.commit;
          if (remoteId && remoteId !== __BUILD_ID__) {
            try { localStorage.setItem('perfolab_update_pending', '1'); } catch {}
          }
        } catch {}
      };
      setTimeout(checkVersionMismatch, 5000);
      setInterval(checkVersionMismatch, 15 * 60 * 1000);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkVersionMismatch();
      });
    }

    try { sessionStorage.removeItem('perfolab_boot_reload'); } catch {}
    root.render(<App />);
  }).catch((err) => {
    console.error('[bootstrap] No se pudo cargar la aplicación:', err);
    try {
      if (!sessionStorage.getItem('perfolab_boot_reload')) {
        sessionStorage.setItem('perfolab_boot_reload', '1');
        window.location.reload();
        return;
      }
    } catch {}
    rootElement.innerHTML =
      '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui;color:#e5e7eb;background:#0b0f14;text-align:center;padding:24px">No se pudo cargar PERFOLAB. Revisa tu conexión y vuelve a intentar.</div>';
  });
}
